import { useTranslation } from 'react-i18next'
import { Play, ShieldAlert, ShieldCheck, Eye, X, KeyRound } from 'lucide-react'
import { commandLikelyNeedsSudo } from '../../hooks/useTerminalAI'

const riskConfig = {
  read: {
    icon: Eye,
    labelKey: 'terminalAi.cmd.labelRead',
    border: 'border-emerald-700/50',
    bg: 'bg-emerald-900/20',
    badge: 'bg-emerald-800 text-emerald-300',
    settingKey: 'autoApproveReads',
    checkLabelKey: 'terminalAi.cmd.autoApproveReads',
    checkColor: 'text-emerald-400',
  },
  write: {
    icon: ShieldCheck,
    labelKey: 'terminalAi.cmd.labelWrite',
    border: 'border-amber-700/50',
    bg: 'bg-amber-900/20',
    badge: 'bg-amber-800 text-amber-300',
    settingKey: 'autoApproveWrites',
    checkLabelKey: 'terminalAi.cmd.autoApproveWrites',
    checkColor: 'text-amber-400',
  },
  dangerous: {
    icon: ShieldAlert,
    labelKey: 'terminalAi.cmd.labelDangerous',
    border: 'border-red-700/50',
    bg: 'bg-red-900/20',
    badge: 'bg-red-800 text-red-300',
    settingKey: null,
    checkLabelKey: null,
    checkColor: null,
  },
}

export default function TerminalAICommandCard({
  command,
  onExecute,
  onReject,
  disabled,
  settings,
  onSettingsChange,
  isLinuxProfile,
}) {
  const { t } = useTranslation()
  const config = riskConfig[command.riskLevel] || riskConfig.write
  const Icon = config.icon

  const showAutoApproveHint =
    command.requiresConfirmation &&
    command.riskLevel !== 'dangerous' &&
    config.settingKey &&
    settings &&
    onSettingsChange &&
    !settings[config.settingKey]

  // Indicação proativa: comando provavelmente precisa de sudo
  const needsSudo =
    isLinuxProfile &&
    commandLikelyNeedsSudo(command.command)

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-3 space-y-2`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 flex flex-col gap-1">
          <code className="text-xs font-mono text-slate-200 bg-slate-900/60 rounded px-2 py-1.5 break-all">
            {command.command}
          </code>
          {/* Badge sudo proativo */}
          {needsSudo && (
            <span className="inline-flex items-center gap-1 self-start rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-900/30 border border-amber-700/30 text-amber-400">
              <KeyRound className="h-2.5 w-2.5" />
              {t('terminalAi.cmd.mayNeedSudo')}
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase shrink-0 ${config.badge}`}
        >
          <Icon className="h-3 w-3" />
          {t(config.labelKey)}
        </span>
      </div>

      {command.explanation && (
        <p className="text-xs text-slate-400">{command.explanation}</p>
      )}

      {command.requiresConfirmation && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex gap-2">
            <button
              onClick={() => onExecute(command.command)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-theme-primary px-3 py-1 text-xs font-medium text-white transition hover:bg-theme-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Play className="h-3 w-3" />
              {t('terminalAi.cmd.execute')}
            </button>
            <button
              onClick={() => onReject?.(command.command)}
              disabled={disabled}
              className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-3 py-1 text-xs font-medium text-slate-300 transition hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="h-3 w-3" />
              {t('terminalAi.cmd.ignore')}
            </button>
          </div>

          {/* Checkbox inline para habilitar auto-approve */}
          {showAutoApproveHint && (
            <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={false}
                onChange={(e) =>
                  onSettingsChange({ ...settings, [config.settingKey]: e.target.checked })
                }
                className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-700 focus:ring-offset-0"
              />
              <span className={`text-[10px] ${config.checkColor}`}>
                {t(config.checkLabelKey)}
              </span>
            </label>
          )}
        </div>
      )}

      {!command.requiresConfirmation && (
        <p className="text-[10px] text-slate-500 italic">{t('terminalAi.cmd.executedAutomatically')}</p>
      )}
    </div>
  )
}
